import { useEffect, useState } from 'react';
import { apiGet } from '../../api/apiClient';
import {
    FLOW_GROUP_IDS,
    type FlowGroupId,
    type LatestFlow,
    type LatestFlowByMarket,
} from './groups';

interface LatestEntry {
    market_id: string;
    group: string;
    ts: string | null;
    n_traders_yes: number;
    n_traders_no: number;
    capital_usd_yes: number;
    capital_usd_no: number;
}

interface LatestResponse {
    groups: string[];
    entries: LatestEntry[];
}

export interface LatestFlowResult {
    byMarket: LatestFlowByMarket;
    loading: boolean;
    loaded: boolean;
    /** The request for this event has finished, with an answer or an error. */
    settled: boolean;
}

const EMPTY: LatestFlowByMarket = new Map();
const MAX_AGE_DAYS = 90;

function isFlowGroup(group: string): group is FlowGroupId {
    return (FLOW_GROUP_IDS as readonly string[]).includes(group);
}

export function toLatestFlow(
    entries: readonly LatestEntry[]
): LatestFlowByMarket {
    const byMarket = new Map<string, Map<FlowGroupId, LatestFlow>>();
    for (const e of entries) {
        if (!isFlowGroup(e.group)) continue;
        let inner = byMarket.get(e.market_id);
        if (!inner) {
            inner = new Map();
            byMarket.set(e.market_id, inner);
        }
        inner.set(e.group, {
            netCapital: e.capital_usd_yes - e.capital_usd_no,
            capitalYes: e.capital_usd_yes,
            capitalNo: e.capital_usd_no,
            wallets: e.n_traders_yes + e.n_traders_no,
            ts: e.ts ? new Date(e.ts).getTime() : null,
        });
    }
    return byMarket;
}

export function useLatestFlow(eventId: string | null): LatestFlowResult {
    const [byMarket, setByMarket] = useState<LatestFlowByMarket>(EMPTY);
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);
    // The event whose request has finished. Compared with the event asked
    // for, so the first render with a new event is not taken as settled
    // before its effect has even started the request.
    const [settledFor, setSettledFor] = useState<string | null>(null);

    useEffect(() => {
        setByMarket(EMPTY);
        setLoaded(false);
        // A new request is not settled by an earlier one's answer.
        setSettledFor(null);
        if (!eventId) {
            setLoading(false);
            return;
        }
        const controller = new AbortController();
        setLoading(true);
        const params = new URLSearchParams({
            event_id: eventId,
            groups: FLOW_GROUP_IDS.join(','),
            max_age_days: String(MAX_AGE_DAYS),
        });
        apiGet<LatestResponse>(
            `/api/screener/conviction/latest?${params}`,
            controller.signal
        )
            .then(res => {
                if (controller.signal.aborted) return;
                setByMarket(toLatestFlow(res.entries ?? []));
                setLoaded(true);
                setLoading(false);
                setSettledFor(eventId);
            })
            .catch(err => {
                if (controller.signal.aborted || err?.name === 'AbortError')
                    return;
                console.error('latest flow failed:', err);
                setLoading(false);
                setSettledFor(eventId);
            });
        return () => controller.abort();
    }, [eventId]);

    return {
        byMarket,
        loading,
        loaded,
        settled: eventId !== null && settledFor === eventId,
    };
}
